'use client';

import Image from 'next/image';
import Slider from 'react-slick';
import { twMerge } from 'tailwind-merge';
import { faChevronsRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

import { IMetaData } from '@lamesarv-sdk/types';
import { IComponentHero } from '@/types/cms';

import { HeroForm } from './HeroForm';

const imagePositionsClasses: Record<string, string> = {
  left: 'object-left',
  center: 'object-center',
  right: 'object-right',
};

interface ICustomArrowProps {
  isRight?: boolean;
  onClick?: () => void;
}

const CustomArrow = ({ isRight, onClick }: ICustomArrowProps) => (
  <button
    type="button"
    onClick={onClick}
    className={twMerge(
      'absolute top-1/2 -translate-y-1/2 flex items-center justify-center cursor-pointer z-10 outline-none size-9 md:size-7 lg:size-10 md:rounded-full text-white max-md:hover:text-gray-100 md:text-black-500 md:bg-white md:hover:bg-gray-100',
      isRight ? 'right-0 md:right-4 lg:right-8' : 'left-0 md:left-4 lg:left-8',
    )}
  >
    <FontAwesomeIcon
      className="size-6 drop-shadow-[0px_0px_3px_0px_#00000080] md:hidden"
      icon={isRight ? faChevronRight : faChevronLeft}
    />
    <FontAwesomeIcon className="size-3 lg:size-4 max-md:hidden" icon={isRight ? faArrowRight : faArrowLeft} />
  </button>
);

const CustomPaging = () => (
  <div className="size-3 md:size-2.5 lg:size-3.5 rounded-full border border-white p-0.5 cursor-pointer hover:border-gray-100">
    <figure className="size-full rounded-full" />
  </div>
);

interface IHeroProps extends IComponentHero {
  metadata: IMetaData;
}

export const Hero = ({
  disable_pause_on_hover,
  display_arrows,
  display_dots,
  images,
  metadata,
  transition_fade,
  transition_vertical,
  transition_speed: speed,
  urls,
  classes,
}: IHeroProps) => {
  const autoplaySpeed = speed ? speed * 1000 : undefined;

  const displayArrows =
    display_arrows === undefined || display_arrows === 'default' ? images.length > 1 : display_arrows === 'true';
  const displayDots =
    display_dots === undefined || display_dots === 'default' ? images.length > 1 : display_dots === 'true';

  const handleLinkClick = (url: string): void => {
    if (url.startsWith('#')) {
      document.getElementById(url.slice(1))?.scrollIntoView({
        behavior: 'smooth',
      });
    } else {
      window.location.href = url;
    }
  };

  return (
    <div className={twMerge('relative w-full -mb-2', classes)}>
      <Slider
        slidesToScroll={1}
        slidesToShow={1}
        infinite={images.length > 1}
        arrows={displayArrows}
        nextArrow={<CustomArrow isRight />}
        prevArrow={<CustomArrow />}
        dots={displayDots}
        pauseOnHover={disable_pause_on_hover ? false : true}
        dotsClass="absolute bottom-5 md:bottom-3 lg:bottom-5 right-1/2 !flex gap-1.5 translate-x-1/2 [&>li.slick-active_figure]:!bg-white z-10"
        customPaging={CustomPaging}
        adaptiveHeight
        autoplay
        autoplaySpeed={autoplaySpeed || 3000}
        fade={transition_fade}
        vertical={transition_vertical}
      >
        {images?.map((image, index) => {
          return (
            <div key={index} className="w-full h-[400px] md:h-[525px] relative">
              <Image
                layout="fill"
                src={image.image_url ?? ''}
                alt=""
                className={twMerge(
                  'object-cover object-center object-',
                  (image.image_position && imagePositionsClasses[image.image_position]) || image.image_position,
                  image.image_position_classes,
                )}
              />

              {(image.overlay_text || image.button_text) && (
                <div className="flex flex-col items-start absolute top-20 left-[31rem] right-20 gap-8">
                  {image.overlay_text && (
                    <span
                      className={twMerge(
                        'text-white text-6xl leading-tight font-bold drop-shadow-md',
                        image.overlay_classes,
                      )}
                    >
                      {image.overlay_text}
                    </span>
                  )}
                  {image.button_text && (
                    <a
                      href={image.button_link}
                      onClick={
                        image.button_link
                          ? (e) => {
                              e.preventDefault();
                              handleLinkClick(image.button_link);
                            }
                          : undefined
                      }
                      className={twMerge(
                        'block text-center bg-black text-white py-4 px-12 rounded-sm uppercase',
                        image.button_classes,
                      )}
                    >
                      {image.button_text}
                    </a>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </Slider>
      <HeroForm metadata={metadata} />
      {!!urls && (
        <div className="right-0 z-10 flex flex-col justify-end md:absolute top-2 lg:top-16">
          {urls.map((link, index) => {
            return (
              <a
                key={index}
                className={
                  'flex flex-row pr-1 pl-3 md:mb-2 mb-px text-white text-sm font-semibold leading-10 bg-blue-800 uppercase opacity-90 hover:opacity-80 hover:bg-sage-800'
                }
                href={link.url}
                onClick={(e) => {
                  e.preventDefault();
                  handleLinkClick(link.url);
                }}
              >
                <span className="grow">{link.title}</span>
                <FontAwesomeIcon
                  icon={faChevronsRight}
                  className="shrink h-5 w-5 mt-3 ml-4 block'} aria-hidden={'true"
                />
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
};
